import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, Select, message, Space, Popconfirm, Card, Row, Col, Tag, Tooltip, Switch } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, ReloadOutlined, ClockCircleOutlined, ExperimentOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { superAdminService } from '../../services';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './Doctors.css';

const Doctors = () => {
    const [doctors, setDoctors] = useState([]);
    const [filteredDoctors, setFilteredDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        setLoading(true);
        try {
            const response = await superAdminService.getDoctors();
            setDoctors(response.data);
            setFilteredDoctors(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        let filtered = doctors;

        if (values.name) {
            filtered = filtered.filter(d =>
                d.name.toLowerCase().includes(values.name.toLowerCase())
            );
        }

        if (values.phoneNumber) {
            filtered = filtered.filter(d =>
                d.phoneNumber.includes(values.phoneNumber)
            );
        }

        if (values.specialization) {
            filtered = filtered.filter(d =>
                d.specialization && d.specialization.toLowerCase().includes(values.specialization.toLowerCase())
            );
        }

        setFilteredDoctors(filtered);
    };

    const handleReset = () => {
        setFilteredDoctors(doctors);
    };

    const handleCreate = () => {
        setEditingDoctor(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingDoctor(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleToggleActive = async (record) => {
        try {
            await superAdminService.updateDoctor(record._id, { isActive: !record.isActive });
            message.success(record.isActive
                ? (t('doctorSuspended') || 'Doctor suspended')
                : (t('doctorActivated') || 'Doctor activated'));
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingDoctor) {
                await superAdminService.updateDoctor(editingDoctor._id, values);
            } else {
                await superAdminService.createDoctor(values);
            }
            message.success(t('success'));
            setModalVisible(false);
            form.resetFields();
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleRenew = async (id) => {
        try {
            await superAdminService.renewDoctor(id);
            message.success('Doctor account renewed for 1 year');
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to renew account');
        }
    };

    const handleToggleAI = async (record, checked) => {
        try {
            await superAdminService.setDoctorAiAccess(record._id, checked);
            message.success(checked ? (t('aiEnabledMsg') || 'AI features enabled') : (t('aiDisabledMsg') || 'AI features disabled'));
            setDoctors((prev) => prev.map((d) => d._id === record._id ? { ...d, aiEnabled: checked } : d));
            setFilteredDoctors((prev) => prev.map((d) => d._id === record._id ? { ...d, aiEnabled: checked } : d));
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('email'),
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: t('specialization'),
            dataIndex: 'specialization',
            key: 'specialization'
        },
        {
            title: t('language'),
            dataIndex: 'language',
            key: 'language',
            render: (lang) => lang === 'en' ? t('english') : lang === 'ar' ? t('arabic') : t('kurdish')
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: (expiryDate, record) => {
                if (!expiryDate) return '-';
                const daysLeft = dayjs(expiryDate).diff(dayjs(), 'day');
                const isExpired = record.isExpired || daysLeft < 0;
                
                return (
                    <Space direction="vertical" size={0}>
                        <span>{dayjs(expiryDate).format('YYYY-MM-DD')}</span>
                        {isExpired ? (
                            <Tag color="red" icon={<ClockCircleOutlined />}>Expired</Tag>
                        ) : daysLeft < 30 ? (
                            <Tag color="orange" icon={<ClockCircleOutlined />}>{daysLeft} days left</Tag>
                        ) : (
                            <Tag color="green">{daysLeft} days left</Tag>
                        )}
                    </Space>
                );
            }
        },
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive, record) => {
                if (record.isExpired) {
                    return <Tag color="red">Expired</Tag>;
                }
                return isActive ? <Tag color="green">{t('active')}</Tag> : <Tag color="default">{t('inactive')}</Tag>;
            }
        },
        {
            title: <Tooltip title={t('aiAccessHint') || 'Allow this doctor to use AI features'}><span><ExperimentOutlined /> {t('aiAccess') || 'AI Access'}</span></Tooltip>,
            key: 'aiEnabled',
            render: (_, record) => (
                <Switch
                    checked={Boolean(record.aiEnabled)}
                    onChange={(checked) => handleToggleAI(record, checked)}
                    checkedChildren={t('on') || 'On'}
                    unCheckedChildren={t('off') || 'Off'}
                />
            )
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/superadmin/reports?doctor=${record._id}`)}
                    />
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    {record.isExpired && (
                        <Tooltip title="Renew for 1 year">
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={() => handleRenew(record._id)}
                                style={{ backgroundColor: '#52c41a' }}
                            />
                        </Tooltip>
                    )}
                    <Popconfirm
                        title={record.isActive
                            ? (t('confirmSuspend') || 'Suspend this doctor?')
                            : (t('confirmActivate') || 'Activate this doctor?')}
                        onConfirm={() => handleToggleActive(record)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        {record.isActive ? (
                            <Tooltip title={t('suspend') || 'Suspend'}>
                                <Button icon={<StopOutlined />} danger />
                            </Tooltip>
                        ) : (
                            <Tooltip title={t('activate') || 'Activate'}>
                                <Button icon={<CheckCircleOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }} />
                            </Tooltip>
                        )}
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} sm={16} md={12}>
                    <h1 className="page-title">{t('doctors')}</h1>
                </Col>
                <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        size="large"
                    >
                        {t('createDoctor')}
                    </Button>
                </Col>
            </Row>

            <Card className="search-filter-card">
                <SearchFilter
                    searchFields={[
                        { key: 'name', label: t('name'), placeholder: t('searchBy') + ' ' + t('name') },
                        { key: 'phoneNumber', label: t('phoneNumber'), placeholder: t('searchBy') + ' ' + t('phoneNumber') },
                        { key: 'specialization', label: t('specialization'), placeholder: t('searchBy') + ' ' + t('specialization') }
                    ]}
                    filterFields={[]}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filteredDoctors}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 1000 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                />
            </Card>

            <FormModal
                title={editingDoctor ? t('edit') : t('createDoctor')}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onSubmit={handleSubmit}
                form={form}
                width={800}
                className="form-modal-two-column"
            >
                <Form.Item
                    name="name"
                    label={t('name')}
                    rules={[{ required: true, message: t('name') + ' ' + t('required') }]}
                >
                    <Input placeholder={t('name')} />
                </Form.Item>

                <Form.Item
                    name="phoneNumber"
                    label={t('phoneNumber')}
                    rules={[{ required: true, message: t('phoneNumber') + ' ' + t('required') }]}
                >
                    <Input disabled={!!editingDoctor} placeholder={t('phoneNumber')} />
                </Form.Item>

                <Form.Item
                    name="email"
                    label={t('email')}
                    rules={[{ required: true, type: 'email', message: t('email') + ' ' + t('required') }]}
                >
                    <Input placeholder={t('email')} type="email" />
                </Form.Item>

                {!editingDoctor && (
                    <Form.Item
                        name="password"
                        label={t('password')}
                        rules={[{ required: true, message: t('password') + ' ' + t('required') }]}
                    >
                        <Input.Password placeholder={t('password')} />
                    </Form.Item>
                )}

                <Form.Item name="specialization" label={t('specialization')}>
                    <Input placeholder={t('specialization')} />
                </Form.Item>

                <Form.Item
                    name="language"
                    label={t('language')}
                    rules={[{ required: true, message: t('language') + ' ' + t('required') }]}
                >
                    <Select placeholder={t('select')}>
                        <Select.Option value="en">{t('english')}</Select.Option>
                        <Select.Option value="ar">{t('arabic')}</Select.Option>
                        <Select.Option value="ku">{t('kurdish')}</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item name="address" label={t('address')} className="ant-form-item-full">
                    <Input.TextArea placeholder={t('address')} rows={2} />
                </Form.Item>

                <Form.Item name="clinicAddress" label={t('clinicAddress')} className="ant-form-item-full">
                    <Input.TextArea placeholder={t('clinicAddress')} rows={2} />
                </Form.Item>

                <Form.Item name="clinicPhone" label={t('clinicPhone')}>
                    <Input placeholder={t('clinicPhone')} />
                </Form.Item>
            </FormModal>
        </div>
    );
};

export default Doctors;
